import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  useCreateFinanceEntryDocumentsMutation,
  useFetchDocumentsFinanceEntriesQuery,
  useToast,
} from "@/hooks";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialogDelete, Pagination } from "@/components/common";
import { useTranslation } from "react-i18next";
import { CreateDocumentRequestFinanceEntry } from "@/types";
import { Download, Trash2 } from "lucide-react";
import { DisplayError } from "@/utils";

type DocumentsFinanceEntriesProps = {
  id: string;
};

const MAX_FILE_SIZE = 10_000_000;
type FileExtensions = {
  [key: string]: string[];
};
const validFileExtensions: FileExtensions = {
  File: [
    "txt",
    "pdf",
    "docx",
    "xls",
    "xlsx",
    "csv",
    "xlt",
    "xltx",
    "jpg",
    "jpeg",
    "png",
  ],
};

export function DocumentsFinanceEntries({ id }: DocumentsFinanceEntriesProps) {
  const { t } = useTranslation();

  const { toast } = useToast();

  const [searchParams] = useSearchParams();
  const page = searchParams.get("current_page_documents");

  const [files, setFiles] = useState<File | null>(null);

  const initialValues = {
    attachment_path: "",
    attachment_description: "",
  };

  const {
    data: documents,
    refetch,
    isLoading: isLoadingDocuments,
  } = useFetchDocumentsFinanceEntriesQuery({
    financeEntriesId: id,
    page,
  });

  const { mutateAsync, isLoading } = useCreateFinanceEntryDocumentsMutation();

  function isValidFileType(fileName: string, fileType: string) {
    return (
      fileName &&
      validFileExtensions[fileType].indexOf(fileName.split(".").pop() || "") >
      -1
    );
  }

  const validation = Yup.object().shape({
    attachment_description: Yup.string().optional(),
    attachment_path: Yup.mixed()
      .required(t("LABEL.required"))
      .test("fileType", t("LABEL.fileTypeError"), (value) => {
        if (value && value instanceof File) {
          return isValidFileType(value.name.toLowerCase(), "File")
            ? true
            : false;
        }
        return true;
      })
      .test("fileSize", t("LABEL.fileSizeMax"), (value) => {
        if (value && value instanceof File) {
          return value.size <= MAX_FILE_SIZE;
        }
        return true;
      }),
  });

  const { resetForm, handleSubmit, ...formik } = useFormik({
    initialValues,
    validationSchema: validation,
    onSubmit: async (values) => {
      handleCreateDocumentSubmit(values);
    },
  });

  async function handleCreateDocumentSubmit(
    values: CreateDocumentRequestFinanceEntry
  ) {
    try {
      const fd = new FormData();
      if (files) {
        fd.append("attachment_path", files);
      }
      fd.append("attachment_description", values.attachment_description || "");

      await mutateAsync(
        { id: id, request: fd },
        {
          onSuccess: () => {
            toast({
              title: t("LABEL.success"),
              description: t("MESSAGE.success"),
            });
            resetForm();
          },
          onError: (error) => {
            toast({
              title: t("LABEL.error"),
              description: DisplayError(error, t("MESSAGE.error_message")),
              variant: "destructive",
            });
          },
        }
      );
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  function handleDownload(filePath: any) {
    if (filePath) {
      const link = document.createElement("a");
      link.href = filePath;

      link.download = Math.random().toString();

      link.click();

      link.remove();
    }
  }

  useEffect(() => {
    refetch();
  }, [refetch, id, page]);

  return (
    <section className="w-full flex flex-col gap-2 p-4">
      <form
        onSubmit={handleSubmit}
        className="flex py-5 px-7 rounded-lg border border-dashed border-zinc-200 items-center gap-4"
      >
        <div className="flex-1 flex flex-col gap-2">
          <Label>Nome</Label>
          <Input
            {...formik.getFieldProps("attachment_description")}
            isInvalid={
              formik.touched.attachment_description &&
              !!formik.errors.attachment_description
            }
            isValid={
              formik.touched.attachment_description &&
              !formik.errors.attachment_description
            }
            messageError={formik.errors.attachment_description}
          />
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <Label>Arquivo</Label>
          <Input
            type="file"
            isInvalid={
              formik.touched.attachment_path && !!formik.errors.attachment_path
            }
            isValid={
              formik.touched.attachment_path && !formik.errors.attachment_path
            }
            messageError={formik.errors.attachment_path}
            onChange={(event) => {
              const file = event.target.files && event.target.files[0];
              if (file) {
                setFiles(file);
                formik.setFieldValue("attachment_path", file);
                const fileReader = new FileReader();
                fileReader.onload = () => {
                  if (fileReader.readyState === 2) {
                    formik.setFieldTouched("file", true);
                    formik.setFieldValue("attachment_path", fileReader.result);
                  }
                };
                fileReader.readAsDataURL(file);
              } else {
                formik.setFieldValue("attachment_path", "");
              }
            }}
          />
        </div>
        <Button
          className="h-10"
          isLoading={isLoading}
          disabled={formik.isSubmitting || isLoading}
        >
          {t("LABEL.save")}
        </Button>
      </form>
      <main className="flex flex-col gap-2">
        {documents && !isLoadingDocuments ? (
          <div className="w-full mx-auto p-4">
            <div className="rounded-2xl border border-slate-200/60 bg-white overflow-hidden">
              <div className="max-h-[500px] overflow-auto">
                <Table className="border-separate border-spacing-0">
                  <TableHeader className="sticky top-0 z-20 bg-teal-700">
                    <TableRow>
                      <TableHead className="text-white font-semibold text-sm uppercase tracking-wide py-4">
                        {t("LABEL.file")}
                      </TableHead>
                      <TableHead className="text-white font-semibold text-sm uppercase tracking-wide py-4 w-[120px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.items.map((item, index) => (
                      <TableRow
                        key={index}
                        className="transition-all duration-300 ease-in-out hover:bg-gradient-to-r hover:from-teal-50/80 hover:to-white data-[state=selected]:bg-teal-50"
                      >
                        <TableCell className="py-4 px-4 text-sm text-slate-600 font-medium">
                          {`${item.attachment_description}.${item.attachment_path.split(".").pop() ?? ""
                            }`}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-teal-600 hover:bg-teal-100 hover:text-teal-700 transition-all duration-200"
                              onClick={() =>
                                handleDownload(item.attachment_path)
                              }
                            >
                              <Download size={16} />
                            </Button>
                            <AlertDialogDelete
                              endpoint={`/attachments/finance-entries/${item.id}`}
                            >
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:bg-red-100 hover:text-red-600 transition-all duration-200"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </AlertDialogDelete>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <Pagination
              data={documents.data}
              type="custom"
              type_string="current_page_documents"
            />
          </div>
        ) : (
          <Skeleton className="w-full h-[500px]" />
        )}
      </main>
    </section>
  );
}
